import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Button, Collapse, Divider, Form, Input, Popconfirm, Space, Typography} from 'antd';
import {useDispatch, useIntl, useSelector} from 'umi';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {BranchModel, OverbookingModel, ServiceModel} from '@/typings/models/Location';
import VirtualUserList from '@/components/UserList/VirtualUserList';
import * as LeadSelectors from '@/selectors/leadSelectors';
import {deleteServiceRequest, updateServiceRequest} from '@/services/api/location';
import {processAPIResponseNow} from '@/utils/request';
import ServiceAgents from './ServiceAgents';
import ServiceOperatingHours from './ServiceOperatingHours';
import ServiceMeta from './ServiceMeta';
import styles from './index.less';
import CopyableId from '@/components/CopyableId';
import {CheckAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import {DeleteOutlined} from '@ant-design/icons';
import {getLocationAllowDateTimes} from '@/utils/location';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import {updateExtraFieldsArray} from '@/utils/extraFields';
import ServiceBookingSettings from './ServiceBookingSettings';
import ServiceOverbookingSettings from './ServiceOverbookingSettings';
import NavLabel from '@/components/NavLabel';
import {ServiceTypeActionTag} from '@/components/Filters/ServiceTypeSelect';

const { Panel } = Collapse;

interface ServiceSettingsFormProps {
  service: ServiceModel;
  branch: BranchModel;
  onSuccess?: (data?: any) => void;
}

const ServiceSettingsForm: React.FC<ServiceSettingsFormProps> = ({
                                                                   service,
                                                                   branch,
                                                                   onSuccess,
                                                                 }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  if (!service) return null;

  const fetchLocationLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['location/getBranch'],
  );

  const journeyMetaConfig = useSelector((state: DefaultRootState) =>
    get(state.journey, `config.${branch?.lead_source?.id}.calkit_meta`, []),
  );

  const allAgents = useSelector(LeadSelectors.selectAllAgentsList);

  const metaToBeShown = journeyMetaConfig.filter((m: any) => {
    // Simplified filter logic
    return true;
  });

  const handleLocalUpdate = (updatedServiceData: any) => {
    const services = branch.services || branch.branch_services || [];
    const updatedServices = services.map((s) =>
      (s.id || s.uuid) === (service.id || service.uuid) ? { ...s, ...updatedServiceData } : s
    );

    dispatch({
      type: 'location/setBranchDetail',
      payload: {
        branchId: branch.id!,
        branchData: {
          services: updatedServices,
          branch_services: updatedServices,
        },
      },
    });
  };

  // Managers can be added/removed in quick succession — keep the latest list in
  // a ref so a rapid click never computes the next list from a stale render.
  const managersRef = useRef<any[]>(service.managers || []);
  useEffect(() => {
    managersRef.current = service.managers || [];
  }, [service?.id, service?.managers]);

  useEffect(() => {
    if (service) {
      const formValues = {
        name: service.name,
        is_active: service.deleted_at === null,
        managers: (service.managers || []).map((m) => (typeof m === 'string' ? m : m.id)),
        with_video_call: service.with_video_call,
        appointments_duration: service.calendar?.appointments_duration,
        appointment_available_from: service.calendar?.appointment_available_from,
        appointments_until: service.calendar?.appointments_until,
        timeslot_increment: service.calendar?.timeslot_increment,
        appointments_buffer: service.calendar?.appointments_buffer,
      };
      form.setFieldsValue(formValues);
    }
  }, [service?.id, form]);

  const deleteService = () => {
    return deleteServiceRequest({
      params: {
        serviceId: service.id,
      },
    }).then(() => {
      const services = branch.services || branch.branch_services || [];
      const updatedServices = services.filter((s) => (s.id || s.uuid) !== (service.id || service.uuid));
      dispatch({
        type: 'location/setBranchDetail',
        payload: {
          branchId: branch.id!,
          branchData: {
            services: updatedServices,
            branch_services: updatedServices,
          },
        },
      });
      onSuccess?.({ action: 'deleted' });
    });
  };

  const patchQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  function handlePatch(values: any, options?: { skipChangeCheck?: boolean }) {
    // Every PATCH carries a full snapshot of its fields, so concurrent requests
    // can be persisted out of order and silently drop changes — only start a
    // request once the previous one has finished.
    const queued = patchQueueRef.current.then(() => executePatch(values, options));
    patchQueueRef.current = queued.catch(() => undefined);
    return queued;
  }

  async function executePatch(values: any, options?: { skipChangeCheck?: boolean }) {
    // Check if anything actually changed
    const hasChanges = options?.skipChangeCheck || Object.keys(values).some(key => {
      let currentValue: any;
      let newValue = values[key];

      const calendarFields = [
        'appointments_duration',
        'appointment_available_from',
        'appointments_until',
        'timeslot_increment',
        'appointments_buffer',
      ];

      if (calendarFields.includes(key)) {
        currentValue = service.calendar?.[key];
      } else if (key === 'managers') {
        currentValue = (service.managers || []).map((m) => (typeof m === 'string' ? m : m.id));
      } else if (key === 'is_active') {
        currentValue = service.deleted_at === null;
      } else {
        currentValue = service[key];
      }

      return !isEqual(currentValue, newValue);
    });

    if (!hasChanges) return;

    setLoading(true);

    const payload: any = {};
    const calendarFields = [
      'appointments_duration',
      'appointment_available_from',
      'appointments_until',
      'timeslot_increment',
      'appointments_buffer',
    ];

    Object.entries(values).forEach(([key, value]) => {
      if (calendarFields.includes(key)) {
        // The whole calendar rides along so the PATCH is a full snapshot, but the
        // overbooking pair must not: the backend reads those keys as an explicit
        // override, so echoing them back would turn "inherit" into an override on
        // every unrelated booking-settings edit. Overbooking is patched on its own
        // via the top-level `overbooking` key.
        const { additional_capacity, unlimited_booking, ...calendar } =
          service.calendar || {};
        payload.calendar = {
          ...calendar,
          [key]: value,
        };
      } else if (key === 'managers') {
        payload.managers = Array.isArray(value) ? value : [];
      } else if (key === 'is_active') {
        // If we want to handle activation/deactivation via patch
        payload[key] = value;
      } else {
        payload[key] = value;
      }
    });

    const Response = await updateServiceRequest({
      params: { serviceId: service.id },
      payload,
    });

    processAPIResponseNow(Response, {
      showSuccessMessage: true,
      successEntity: INTL_ENTITY.SERVICE,
      onSuccess: (data) => {
        const updatedService = data?.data || { ...service, ...payload };
        handleLocalUpdate(updatedService);
        onSuccess?.(updatedService);
      },
      onEnd: () => {
        setLoading(false);
      },
    });
  }

  // `overbooking` is a top-level key of its own, not a calendar field. The change
  // check is skipped because local state is updated optimistically first, so by
  // the time the request fires there is no diff left to detect.
  const debouncedOverbookingPatch = useMemo(
    () =>
      debounce((overbooking: OverbookingModel) => {
        patchRef.current({ overbooking }, { skipChangeCheck: true });
      }, 500),
    [service?.id],
  );

  useEffect(() => () => debouncedOverbookingPatch.flush(), [debouncedOverbookingPatch]);

  const handleOverbookingChange = (overbooking: OverbookingModel) => {
    handleLocalUpdate({ overbooking });
    debouncedOverbookingPatch(overbooking);
  };

  // Updated in an effect (not during render) so that a flush() running in an
  // effect cleanup on service switch still patches the previous service.
  const patchRef = useRef(handlePatch);
  useEffect(() => {
    patchRef.current = handlePatch;
  });

  // Coalesce rapid manager additions/removals into a single request carrying
  // the final list. The change check is skipped because local state is updated
  // optimistically before the request fires, so by then there is no diff left.
  const debouncedManagersPatch = useMemo(
    () =>
      debounce((managerIds: string[]) => {
        patchRef.current({ managers: managerIds }, { skipChangeCheck: true });
      }, 500),
    [service?.id],
  );

  useEffect(() => () => debouncedManagersPatch.flush(), [debouncedManagersPatch]);

  const detailsForm = (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(changedValues) => {
        const skipBlur = ['is_active', 'with_video_call'];
        const [key] = Object.keys(changedValues);
        if (skipBlur.includes(key)) {
          handlePatch(changedValues);
        }
      }}
    >
      <NavLabel strong size={'large'} noPadding>
        <F id="common.general" defaultMessage="General" />
      </NavLabel>
      {service?.service_type && (
        <Space style={{ marginTop: 16, marginBottom: 24 }}>
          <Typography.Text color={'secondary'}>
            <F id={'entity.service_type.singular'} />:
          </Typography.Text>
          <ServiceTypeActionTag serviceType={service.service_type} />
        </Space>
      )}
      <Form.Item
        label={
          <F id="pages.location.service.name" defaultMessage="Service Name" />
        }
        name="name"
        rules={[{ required: true }]}
        tooltip={intl.formatMessage({
          id: 'pages.location.service.nameTooltip',
          defaultMessage:
            'This name is only used for external booking and customer interactions.',
        })}
      >
        <Input onBlur={(e) => handlePatch({ name: e.target.value })} />
      </Form.Item>

      <Form.Item
        label={
          <F
            id="pages.locations.branchServiceManager"
            defaultMessage="Service Manager"
          />
        }
        name="managers"
        tooltip={intl.formatMessage({
          id: 'pages.locations.branchServiceManagerTooltip',
          defaultMessage:
            'The manager of the service. This person will be responsible for the automations created related to this service.',
        })}
      >
        <VirtualUserList
          users={(service.managers || []).map((m) => {
            if (typeof m === 'string') {
              return allAgents.find((a) => a.id === m) || { id: m };
            }
            return m;
          }) as any}
          onAdd={(id, user) => {
            const currentManagers = managersRef.current || [];
            const currentManagerIds = currentManagers.map((m) => (typeof m === 'string' ? m : m.id));
            if (currentManagerIds.includes(id)) return;
            const nextManagers = [...currentManagers, user ?? id];
            managersRef.current = nextManagers;
            handleLocalUpdate({ managers: nextManagers });
            debouncedManagersPatch([...currentManagerIds, id]);
          }}
          onRemove={(id) => {
            const nextManagers = (managersRef.current || []).filter(
              (m) => (typeof m === 'string' ? m : m.id) !== id,
            );
            const nextManagerIds = nextManagers.map((m) => (typeof m === 'string' ? m : m.id));
            managersRef.current = nextManagers;
            handleLocalUpdate({ managers: nextManagers });
            debouncedManagersPatch(nextManagerIds);
          }}
        />
      </Form.Item>

      <ServiceBookingSettings form={form} onPatch={handlePatch} />
    </Form>
  );
  const hasAgents = service?.agents?.length > 0;
  const hasOperatingHours = getLocationAllowDateTimes(service)?.length > 0;

  // Must run on submit, not on every keystroke: an optimistic write while typing
  // makes ExtraFieldItem's own change check see no diff on blur, so it never patches.
  const handleExtraFieldSubmit = (values: any) => {
    if (!service?.extra_fields) return;
    const updatedExtraFields = updateExtraFieldsArray(service.extra_fields as any, values);
    handleLocalUpdate({ extra_fields: updatedExtraFields });
  };

  return (
    <div className={styles.formContainer}>
      {!hasAgents && !hasOperatingHours && (
        <Alert
          message={intl.formatMessage({
            id: 'pages.locations.service.missingConfigWarning',
            defaultMessage:
              'Agents and Operating Hours are not configured for this service. Please configure them to enable booking.',
          })}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {detailsForm}

      <Collapse ghost expandIconPosition="right">
        <Panel
          header={<F id="entity.agent.plural" defaultMessage="Agents" />}
          key="agents"
        >
          <ServiceAgents
            service={service}
            branch={branch}
            onSuccess={handleLocalUpdate}
          />
        </Panel>

        <Panel
          header={
            <F
              id="pages.locations.operatingHours"
              defaultMessage="Operating Hours"
            />
          }
          key="hours"
        >
          <ServiceOperatingHours
            service={service}
            branch={branch}
            onSuccess={handleLocalUpdate}
            branchLoading={fetchLocationLoading}
          />
        </Panel>

        <Panel
          header={<F id="common.advanced" defaultMessage="Advanced" />}
          key="advanced"
        >
          <ServiceOverbookingSettings
            value={service.overbooking}
            serviceType={service.service_type}
            onChange={handleOverbookingChange}
          />
        </Panel>

        {!isEmpty(metaToBeShown) && (
          <Panel
            header={<F id="pages.locations.meta" defaultMessage="Meta" />}
            key="meta"
          >
            <ServiceMeta
              service={service}
              branch={branch}
              onSuccess={handleLocalUpdate}
            />
          </Panel>
        )}
        {(service as any)?.extra_fields?.length > 0 && (
          <Panel
            header={
              <F id="entity.extra_field.plural" defaultMessage="Extra Fields" />
            }
            key="extra_fields"
          >
            <DynamicExtraFieldsForm
              labelWidth={210}
              fields={(service as any).extra_fields || []}
              branchServiceId={service?.id}
              onValuesSubmit={handleExtraFieldSubmit}
            />
          </Panel>
        )}
      </Collapse>

      <CopyableId id={service?.id} />
      <Divider size={'small'} />
      <CheckAccess
        access={[
          PERMISSIONS.BranchServices.DELETE,
          PERMISSIONS.BUSINESS_PARTNER,
        ]}
      >
        <Popconfirm
          placement="top"
          onConfirm={deleteService}
          title={
            <F
              entity={INTL_ENTITY.SERVICE}
              action={INTL_ACTION.REMOVE_CONFIRMATION}
            />
          }
        >
          <Button
            color="danger"
            variant="filled"
            block
            icon={<DeleteOutlined />}
          >
            <F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.REMOVE} />
          </Button>
        </Popconfirm>
      </CheckAccess>
    </div>
  );
};

export default ServiceSettingsForm;
